import { format } from "date-fns";
import { Bell, Clock } from "lucide-react";
import { formatReminderTimeRange } from "@/lib/reminder-time";

export interface VisualWeekOccurrence {
  id: string | number;
  title: string;
  coverImage?: string | null;
  reminderTime?: string | null;
  endTime?: string | null;
  eventEndDate?: string | null;
  startDate?: string | null;
  countdownLabel?: string | null;
  isCompleted?: boolean;
  kind?: "reminder" | "calendar-image";
}

export interface VisualWeekDay {
  dateKey: string;
  isToday: boolean;
  isPastDay?: boolean;
  occurrences: VisualWeekOccurrence[];
}

interface VisualWeekCalendarProps {
  days: VisualWeekDay[];
  selectedDateKey?: string | null;
  onDaySelect: (dateKey: string) => void;
  className?: string;
  emptyLabel?: string;
}

function dateFromKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

function formatOccurrenceTime(occurrence: VisualWeekOccurrence): string {
  if (!occurrence.reminderTime) return "";
  return formatReminderTimeRange(occurrence);
}

function imageFallback(title: string): string {
  return title.trim().slice(0, 1).toUpperCase() || "•";
}

export function VisualWeekCalendar({
  days,
  selectedDateKey,
  onDaySelect,
  className = "",
  emptyLabel = "No scheduled activities",
}: VisualWeekCalendarProps) {
  return (
    <div data-testid="visual-week-calendar" className={`overflow-x-auto pb-2 ${className}`}>
      <div className="grid min-w-[760px] grid-cols-7 items-start overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {days.map((day) => {
          const date = dateFromKey(day.dateKey);
          const isSelected = selectedDateKey === day.dateKey;
          const accessibleDetails = day.occurrences.length > 0
            ? day.occurrences
                .map((occurrence) => {
                  const time = formatOccurrenceTime(occurrence);
                  return `${occurrence.title}${time ? `, ${time}` : ""}`;
                })
                .join("; ")
            : emptyLabel;

          return (
            <button
              key={day.dateKey}
              type="button"
              data-testid={`visual-week-day-${day.dateKey}`}
              onClick={() => onDaySelect(day.dateKey)}
              aria-label={`${format(date, "EEEE, MMMM d")}. ${accessibleDetails}`}
              aria-current={day.isToday ? "date" : undefined}
              aria-pressed={isSelected}
              className={[
                "group flex min-h-[260px] w-full self-start flex-col items-stretch justify-start border-r border-slate-200 text-left transition-colors last:border-r-0",
                "focus-visible:z-10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-200",
                day.isToday ? "bg-blue-50/70" : "bg-white hover:bg-slate-50",
                isSelected ? "ring-4 ring-inset ring-blue-500" : "",
              ].join(" ")}
            >
              <div
                data-testid={`visual-week-header-${day.dateKey}`}
                className={[
                  "border-b px-3 py-3",
                  day.isToday
                    ? "border-blue-200 bg-blue-600 text-white"
                    : day.isPastDay
                      ? "border-slate-200 bg-slate-50 text-slate-500"
                      : "border-slate-200 bg-white text-slate-800",
                ].join(" ")}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-extrabold uppercase tracking-[0.12em]">
                    {format(date, "EEE")}
                  </span>
                  {day.isToday && (
                    <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                      Today
                    </span>
                  )}
                </div>
                <span className="mt-1 block text-2xl font-bold leading-none">
                  {format(date, "d")}
                </span>
                <span className="mt-1 block text-[11px] opacity-80">
                  {format(date, "MMM")}
                </span>
              </div>

              <div className="space-y-2 p-2">
                {day.occurrences.length > 0 ? (
                  day.occurrences.map((occurrence) => {
                    const time = formatOccurrenceTime(occurrence);
                    return (
                      <div
                        key={occurrence.id}
                        className={[
                          "overflow-hidden rounded-xl border bg-white shadow-sm",
                          occurrence.isCompleted
                            ? "border-emerald-200 opacity-65"
                            : "border-slate-200",
                        ].join(" ")}
                        title={`${occurrence.title}${time ? ` · ${time}` : ""}`}
                      >
                        <div className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-blue-100 to-indigo-100">
                          {occurrence.coverImage ? (
                            <img
                              src={occurrence.coverImage}
                              alt=""
                              aria-hidden="true"
                              className={`h-full w-full object-cover ${occurrence.isCompleted ? "grayscale" : ""}`}
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                                event.currentTarget.nextElementSibling?.classList.remove("hidden");
                              }}
                            />
                          ) : null}
                          <div className={`${occurrence.coverImage ? "hidden " : ""}absolute inset-0 flex items-center justify-center text-2xl font-extrabold text-blue-700`}>
                            {occurrence.kind === "calendar-image" ? "📅" : imageFallback(occurrence.title)}
                          </div>
                          {occurrence.isCompleted && (
                            <span className="absolute right-1.5 top-1.5 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                              Done
                            </span>
                          )}
                        </div>
                        <div className="space-y-1 px-2 py-1.5">
                          <p className="truncate text-xs font-semibold text-slate-800">
                            {occurrence.title}
                          </p>
                          {time && (
                            <p className="flex items-center gap-1 truncate text-[10px] font-medium text-slate-500">
                              <Clock className="h-3 w-3 shrink-0" />
                              {time}
                            </p>
                          )}
                          {occurrence.countdownLabel && (
                            <p
                              data-testid={`visual-week-countdown-${occurrence.id}`}
                              className="truncate text-[10px] font-bold text-purple-700"
                            >
                              {occurrence.countdownLabel}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex min-h-[150px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 px-2 text-center text-xs text-slate-400">
                    <Bell className="mb-2 h-5 w-5 opacity-50" />
                    <span>{emptyLabel}</span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-center text-xs text-slate-500">
        Tap a day to see its reminder details.
      </p>
    </div>
  );
}
